import { useCallback, useEffect, useRef } from "react";
import { Socket } from "socket.io-client";

import { createAuthedSocket } from "@/utils/createAuthedSocket";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import {
	copywritingOrchestrationSelector,
	setGenerationQueued,
	setStageUpdate,
	setGenerationResult,
	setGenerationError,
} from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import { updateCopywritingInInbox } from "@/pages/Private/redux/list/list.slice";

import {
	CopywritingGenerationError,
	CopywritingGenerationQueuedEvent,
	CopywritingSocketEvent,
	CopywritingStageUpdate,
	CopywritingStatus,
	GenerateCopywritingResponse,
} from "./types";

interface UseCopywritingSocketOptions {
	companyId: number | undefined;
	prospectId: number | undefined;
	enabled: boolean;
}

export const useCopywritingSocket = ({
	companyId,
	prospectId,
	enabled,
}: UseCopywritingSocketOptions) => {
	const dispatch = useAppDispatch();
	const { token } = useAppSelector(authSelector);
	const tokenRef = useRef(token);

	tokenRef.current = token;
	const { listPersonId: orchestrationListPersonId } = useAppSelector(
		copywritingOrchestrationSelector
	);
	const socketRef = useRef<Socket | null>(null);

	// Track listPersonId in a ref so socket callbacks always see the latest value
	// without needing to reconnect when it changes.
	const listPersonIdRef = useRef<number | null>(orchestrationListPersonId);

	useEffect(() => {
		listPersonIdRef.current = orchestrationListPersonId;
	}, [orchestrationListPersonId]);

	const connect = useCallback(() => {
		if (!token || !companyId || !prospectId || !enabled) {
			return;
		}

		// Disconnect existing socket if any
		if (socketRef.current) {
			socketRef.current.disconnect();
		}

		const newSocket = createAuthedSocket(import.meta.env.VITE_API_URL, () => tokenRef.current);

		newSocket.on("connect", () => {
			console.info("[CopywritingSocket] Connected");
			newSocket.emit(CopywritingSocketEvent.JOIN_GENERATION, {
				companyId,
				prospectId,
			});
		});

		newSocket.on("connect_error", (error) => {
			console.warn("[CopywritingSocket] Connection error:", error);
		});

		newSocket.on(
			CopywritingSocketEvent.GENERATION_QUEUED,
			(data: CopywritingGenerationQueuedEvent) => {
				if (data.prospectId === prospectId) {
					dispatch(setGenerationQueued({ ...data, receivedAt: Date.now() }));
					dispatch(
						updateCopywritingInInbox({
							listPersonId: data.listPersonId,
							copywritingStatus: CopywritingStatus.QUEUED,
							copywritingEventId: data.eventId,
							copywritingOutput: null,
							copywritingError: null,
						})
					);
				}
			}
		);

		newSocket.on(CopywritingSocketEvent.STAGE_UPDATE, (data: CopywritingStageUpdate) => {
			if (data.prospectId === prospectId) {
				dispatch(setStageUpdate({ stage: data.stage, label: data.label, receivedAt: Date.now() }));

				const listPersonId = listPersonIdRef.current;

				if (listPersonId !== null) {
					dispatch(
						updateCopywritingInInbox({
							listPersonId,
							copywritingStatus: CopywritingStatus.RUNNING,
						})
					);
				}
			}
		});

		newSocket.on(
			CopywritingSocketEvent.GENERATION_COMPLETE,
			(result: GenerateCopywritingResponse) => {
				if (result.prospectId === prospectId) {
					dispatch(setGenerationResult(result));
					dispatch(
						updateCopywritingInInbox({
							listPersonId: result.listPersonId,
							copywritingStatus: CopywritingStatus.COMPLETED,
							copywritingOutput: result,
							copywritingError: null,
						})
					);
				}
			}
		);

		newSocket.on(CopywritingSocketEvent.GENERATION_ERROR, (data: CopywritingGenerationError) => {
			if (data.prospectId === prospectId) {
				dispatch(setGenerationError(data.error));
				dispatch(
					updateCopywritingInInbox({
						listPersonId: data.listPersonId,
						copywritingStatus: CopywritingStatus.FAILED,
						copywritingError: data.error,
					})
				);
			}
		});

		newSocket.on("disconnect", (reason) => {
			console.warn("[CopywritingSocket] Disconnected:", reason);
		});

		socketRef.current = newSocket;
	}, [token, companyId, prospectId, enabled, dispatch]);

	const disconnect = useCallback(() => {
		if (socketRef.current) {
			socketRef.current.disconnect();
			socketRef.current = null;
		}
	}, []);

	useEffect(() => {
		if (enabled) {
			connect();
		}

		return () => {
			disconnect();
		};
	}, [enabled, connect, disconnect]);

	return { connect, disconnect };
};
